import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiArrowLeft, FiBriefcase, FiDollarSign, FiAlertTriangle, FiUsers, FiPieChart, FiSettings, FiHeadphones, FiShield, FiSave, FiToggleRight, FiToggleLeft, FiLayout, FiCalendar, FiTrendingUp } = FiIcons;

const DepartmentDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { departments = [], companies = [], reports = [], users = [], invoices = [], supportTickets = [], updateDepartment } = useData();
  const [department, setDepartment] = useState(null);
  const [activeView, setActiveView] = useState('overview');
  const [isSaving, setIsSaving] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ name: '', manager_name: '', active: true });

  useEffect(() => {
    if (departments.length > 0 && id) {
      const dept = departments.find(d => d.id === id);
      if (dept) {
        setDepartment(dept);
        setSettingsForm({ 
          name: dept.name || '', 
          manager_name: dept.manager_name || dept.manager || '', 
          active: dept.active ?? true 
        });
      }
    }
  }, [id, departments]);

  if (!department) return (
    <div className="h-screen flex flex-col items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
      <p className="text-gray-500 font-medium">Laster avdelingsdata...</p>
    </div>
  );

  // --- LANDSSPESIFIKK FILTRERING MED DEFENSIV SJEKK ---
  const deptCompanies = (companies || []).filter(c => c && (c.country === department.name || (department.name === 'Norge' && !c.country)));
  const deptCompanyIds = deptCompanies.map(c => c.id);
  
  const internalStaff = (users || []).filter(u => u && (u.department_id === department.id || u.departmentId === department.id));
  const deptUsers = (users || []).filter(u => u && deptCompanyIds.includes(u.companyId));
  const deptInvoices = (invoices || []).filter(inv => inv && deptCompanyIds.includes(inv.companyId));
  const deptReports = (reports || []).filter(r => r && deptCompanyIds.includes(r.companyId));
  const deptTickets = (supportTickets || []).filter(t => t && deptCompanyIds.includes(t.company_id || t.companyId));

  const menuItems = [
    { id: 'overview', label: 'Dashboard', icon: FiPieChart },
    { id: 'companies', label: 'Bedrifter', icon: FiLayout, count: deptCompanies.length },
    { id: 'users', label: 'Kundebrukere', icon: FiUsers, count: deptUsers.length },
    { id: 'internal', label: 'Ansatte', icon: FiShield, count: internalStaff.length },
    { id: 'invoices', label: 'Fakturering', icon: FiDollarSign, count: deptInvoices.length },
    { id: 'reports', label: 'Varsler/Saker', icon: FiAlertTriangle, count: deptReports.length },
    { id: 'support', label: 'Support', icon: FiHeadphones, count: deptTickets.length },
    { id: 'settings', label: 'Innstillinger', icon: FiSettings },
  ];

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    await updateDepartment(department.id, settingsForm);
    setIsSaving(false);
    alert("Innstillinger lagret!");
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-start mb-4">
        <div className={`p-3 rounded-xl ${color} bg-opacity-10`}>
          <SafeIcon icon={Icon} className={color.replace('bg-', 'text-')} />
        </div>
        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{title}</span>
      </div>
      <h3 className="text-3xl font-black text-gray-900">{value}</h3>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b h-16 flex items-center justify-between px-8 sticky top-0 z-30 shadow-sm">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/superadmin/corporate')} className="p-2 hover:bg-gray-100 rounded-lg"><SafeIcon icon={FiArrowLeft} /></button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-black text-xs">{(department.name || '??').substring(0, 2).toUpperCase()}</div>
            <h1 className="text-lg font-black text-gray-900">{department.name} Portal</h1>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-xs font-bold text-gray-500">
          <span className="bg-gray-100 px-3 py-1 rounded-full">Leder: {department.manager_name || department.manager || 'Ikke satt'}</span>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <aside className="w-64 bg-white border-r overflow-y-auto hidden md:block">
          <nav className="p-4 space-y-1">
            {menuItems.map(item => (
              <button key={item.id} onClick={() => setActiveView(item.id)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-bold rounded-xl transition-all ${activeView === item.id ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-500 hover:bg-gray-50'}`}>
                <div className="flex items-center gap-3"><SafeIcon icon={item.icon} /><span>{item.label}</span></div>
                {item.count !== undefined && <span className={`text-[10px] px-2 py-0.5 rounded-full ${activeView === item.id ? 'bg-white/20' : 'bg-gray-100'}`}>{item.count}</span>}
              </button>
            ))}
          </nav>
        </aside>

        <main className="flex-1 overflow-y-auto p-8">
          <AnimatePresence mode="wait">
            {activeView === 'overview' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <StatCard title="Bedrifter" value={deptCompanies.length} icon={FiLayout} color="bg-blue-600" />
                  <StatCard title="Brukere" value={deptUsers.length} icon={FiUsers} color="bg-green-600" />
                  <StatCard title="Saker" value={deptReports.length} icon={FiAlertTriangle} color="bg-red-600" />
                  <StatCard title="Ansatte" value={internalStaff.length} icon={FiShield} color="bg-indigo-600" />
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-white rounded-3xl border p-6">
                    <h3 className="font-black text-xs uppercase tracking-widest text-gray-400 mb-4">Siste saker i {department.name}</h3>
                    {deptReports.length > 0 ? (
                      <div className="space-y-3">
                        {deptReports.slice(0, 5).map(r => (
                          <div key={r.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                            <div><p className="text-sm font-bold">{r.title}</p><p className="text-[10px] text-gray-500">{r.type}</p></div>
                            <span className="text-[10px] font-black uppercase bg-white px-2 py-1 rounded border">{r.status}</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-gray-400 italic text-sm">Ingen saker registrert.</p>}
                  </div>
                  <div className="bg-white rounded-3xl border p-6">
                    <h3 className="font-black text-xs uppercase tracking-widest text-gray-400 mb-4">Interne ansatte ({department.name})</h3>
                    {internalStaff.length > 0 ? (
                      <div className="space-y-3">
                        {internalStaff.map(u => (
                          <div key={u.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl border border-gray-100">
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">{u.name?.charAt(0) || 'U'}</div>
                            <div><p className="text-sm font-bold">{u.name}</p><p className="text-[10px] text-gray-500">{u.role}</p></div>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-gray-400 italic text-sm">Ingen ansatte koblet til dette landet.</p>}
                  </div>
                </div>
              </motion.div>
            )}

            {activeView === 'settings' && (
              <div className="max-w-xl bg-white rounded-3xl border p-8 shadow-sm">
                <h3 className="text-xl font-black mb-6">Land-innstillinger</h3>
                <form onSubmit={handleUpdateSettings} className="space-y-6">
                  <div>
                    <label className="text-[10px] font-black uppercase text-gray-400 mb-1 block">Navn på Land</label>
                    <input type="text" value={settingsForm.name} onChange={e => setSettingsForm({ ...settingsForm, name: e.target.value })} className="w-full p-4 bg-gray-50 border-none rounded-2xl outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase text-gray-400 mb-1 block">Landssjef</label>
                    <input type="text" value={settingsForm.manager_name} onChange={e => setSettingsForm({ ...settingsForm, manager_name: e.target.value })} className="w-full p-4 bg-gray-50 border-none rounded-2xl outline-none" />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                    <span className="font-bold">Status: {settingsForm.active ? 'Aktiv' : 'Deaktivert'}</span>
                    <button type="button" onClick={() => setSettingsForm({ ...settingsForm, active: !settingsForm.active })} className="text-2xl text-blue-600">
                      <SafeIcon icon={settingsForm.active ? FiToggleRight : FiToggleLeft} />
                    </button>
                  </div>
                  <button type="submit" disabled={isSaving} className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black shadow-lg">
                    {isSaving ? 'Lagrer...' : 'Lagre endringer'}
                  </button>
                </form>
              </div>
            )}
            
            {!['overview', 'settings'].includes(activeView) && (
              <div className="bg-white p-20 rounded-3xl border-2 border-dashed flex flex-col items-center text-center">
                <SafeIcon icon={FiLayout} className="text-5xl text-gray-200 mb-4" />
                <h3 className="text-xl font-black text-gray-900">{menuItems.find(m => m.id === activeView)?.label}</h3>
                <p className="text-gray-500 max-w-sm mt-2">Denne modulen filtreres nå automatisk for {department.name}.</p>
              </div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default DepartmentDashboard;